import { warehouseLabel } from '@/lib/warehouseLabel';
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { T, Var, useGT } from 'gt-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import type { BuyingOrderRow, ConfirmCartsInput } from '@erp/rpc';
import type { ReadinessState } from '@erp/shared-utils';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { toast } from 'src/components/ui/toast';
import { SuggestionDetailModal } from './suppliers/SuggestionDetailModal';
import { LineDetailSection } from './components/LineDetailSection';
import {
  readStoredLineTab,
  resolveLineTab,
  storeLineTab,
  type LineTab,
} from './utils/lineTabs';
import Page from 'src/components/page/Page';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import type { MultiSelectOption } from 'src/components/common/MultiSelect';
import { Button } from 'src/components/ui/button';
import useDivisions from 'src/api/organization/useDivisions';
import { useWarehouses } from '@/api/rpc/hooks/inventory-management/useWarehouses';
import {
  useUserPreference,
  useUpsertUserPreference,
} from '@/api/rpc/hooks/core/useUserPreference';
import {
  useBuyingOrders,
  useBuyerCodes,
  useBoardFreshness,
  useBuyingFilterOptions,
  useRefreshReplenishment,
  useConfirmCarts,
  useConfirmTransfers,
  type ConfirmCartsResponse,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { ORPCError } from '@orpc/client';
import { ReleaseTargetChoice } from './components/ReleaseTargetChoice';
import {
  ReleaseFreightAndSending,
  type CartReleaseChoice,
} from './components/ReleaseFreightAndSending';
import type { ReleaseSelection } from './utils/openDraftOrders';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AssignmentBoard,
  type CartCreateSummary,
} from './components/AssignmentBoard';
import {
  cartOwnerId,
  type CartOwner,
} from './components/supplierAssignmentsInput';
import { useCartLines } from './components/useCartLines';
import {
  orderKindTag,
  ownerFilterSummary,
  ownerListFilters,
  selectedOwnerFor,
} from './utils/boardKindFilters';
import { BOARD_PATHS, BoardKindContext } from './components/boardKindContext';
import type { BoardKind } from './utils/buyerBoardColumns';
import { SectionSpinner } from './components/SectionSpinner';
import { BuyingOrdersTable } from './components/BuyingOrdersTable';
import { OrdersViewMenu } from './components/OrdersViewMenu';
import { useColumnsHiddenByFilters } from './components/useColumnsHiddenByFilters';
import { useBuyerBoardColumnConfig } from './components/useBuyerBoardColumnConfig';
import { filterColumnsShownByView } from './utils/buyerBoardColumns';
import { isNeedId, needRepresentativeLine } from './utils/needRows';
import { orderFilterColumns } from './utils/orderFilterColumns';
import { useDebounce } from '@/hooks/useDebounce';
import {
  BUYING_ORDERS_PAGE_SIZE,
  isRowSelected,
  nextBuyingSort,
  pageToOffset,
  parseBuyingSort,
  parseLineTypes,
  selectionForRow,
  buyingRowKey,
  serializeBuyingSort,
  parseGroupBy,
  serializeGroupBy,
  serializeLineTypes,
  ALL_LINE_TYPES,
  type BuyingGroupBy,
  type BuyingOrderSort,
  type BuyingSelectionParams,
} from './utils/buyingOrdersUrl';
import { BoardSectionHeader } from './components/BoardSectionHeader';
import { DetailSection } from './components/DetailSection';
import { BuyerBoardLayout } from './components/BuyerBoardLayout';
import {
  BuyingFilterControls,
  CodeFilter,
  useLineTypeOptions,
} from './components/BuyingFilterControls';
import { BuyingFiltersHeader } from './components/BuyingFiltersHeader';
import { BoardFreshnessIndicator } from './components/BoardFreshnessIndicator';
import { BuyingGroupByToggle } from './components/BuyingGroupByToggle';
import {
  boardSectionsReducer,
  initialSections,
  selectionChangeAction,
  type BoardSectionId,
} from './utils/boardSections';
import { withSelectedCodes } from './utils/codeOptions';
import { useReadinessOptions } from './utils/readinessOptions';
import { releaseCreatedMessage } from './utils/releaseToast';
import {
  BOARD_SHARED_FILTER_KEYS,
  sharedFiltersPatch,
  sharedFiltersSummary,
} from './utils/boardSharedFilters';
import { useBoardSharedFilters } from './utils/useBoardSharedFilters';
import { DemandFilterBar } from '../forecast/demand/DemandFilterBar';
import {
  useDemandFilterLabels,
  useStockStatusLabel,
} from '../forecast/demand/demandFilterLabels';
import { orderSummary } from './utils/orderSummary';
import { filtersSummaryLine } from './utils/filtersSummaryLine';
import { defaultDivisionIds } from './utils/defaultDivisions';
import {
  masterKeyAction,
  masterKeyIntent,
  resolveCursor,
  stepCursor,
  type MasterKeyAction,
  type MasterKeyIntent,
} from './utils/masterKeyboard';
import { ExcelExportButton } from '../components/ExcelExportButton';
import { useExportReplenishmentSuggestions } from 'src/api/rpc/hooks/procurement/useProcurementExports';
// Stable key for an owner (vendor or source) × ship-to cart (in-flight-create
// tracking).
function cartKey(ownerId: string, shipToWarehouseId: string): string {
  return `${ownerId}:${shipToWarehouseId}`;
}

// Closes a detail opened from an old link that carries no ship-to, which has
// no cart header to hold the close button.
function LegacyCloseButton({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex justify-end px-2 pt-2">
      <Button size="sm" variant="ghost" onClick={onClose}>
        <T context="replenishment close detail label">Close</T>
      </Button>
    </div>
  );
}

function formatCurrency(v: number) {
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// Small labeled count pill for the create-orders dialog's coverage stats.
function StatChip({
  value,
  tone,
  children,
}: {
  value: number;
  tone: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        tone,
      )}
    >
      <span className="tabular-nums">{value.toLocaleString()}</span>
      {children}
    </span>
  );
}

// MOV / freight progress bar for the create-orders dialog. Fills toward the
// threshold; green once met. The two thresholds are NOT the same kind of thing:
// `kind: 'hard'` is the vendor MOV — the backend refuses the cart below it and
// pauses for explicit override, so an unmet bar is red. `kind: 'soft'` is the
// prepaid-freight threshold — nothing blocks, you just pay freight, so an unmet
// bar is amber. A vendor with no such threshold still renders a muted row so the
// buyer can tell "no minimum" apart from "not loaded".
// fallow-ignore-next-line complexity
function ThresholdBar({
  label,
  hint,
  value,
  threshold,
  kind,
}: {
  label: ReactNode;
  hint: ReactNode;
  value: number;
  threshold: number | null;
  kind: 'hard' | 'soft';
}) {
  if (threshold == null) {
    return (
      <div className="flex items-center justify-between text-[11px]">
        <span className="font-medium text-gray-500">{label}</span>
        <span className="text-gray-400">{hint}</span>
      </div>
    );
  }
  // A zero threshold is always met — it asks for no additional cart value — so
  // derive `met` from the raw comparison rather than from `pct` (which would be
  // 0 with no denominator to divide by).
  const met = value >= threshold;
  const pct =
    threshold > 0 ? Math.min(100, (value / threshold) * 100) : met ? 100 : 0;
  const gap = Math.max(0, threshold - value);
  const unmetText = kind === 'hard' ? 'text-red-700' : 'text-amber-700';
  const unmetFill = kind === 'hard' ? 'bg-red-500' : 'bg-amber-500';
  return (
    <div>
      <div className="mb-0.5 flex items-baseline justify-between gap-2 text-[11px]">
        <span className="font-medium text-gray-600">
          {label}{' '}
          <span className="font-normal text-gray-400">
            {formatCurrency(threshold)}
          </span>
        </span>
        <span
          className={cn('tabular-nums', met ? 'text-green-700' : unmetText)}
        >
          {met
            ? `${formatCurrency(value)} ✓`
            : `${Math.round(pct)}% (−${formatCurrency(gap)})`}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            met ? 'bg-green-500' : unmetFill,
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      {!met && (
        <div className={cn('mt-0.5 text-[11px]', unmetText)}>{hint}</div>
      )}
    </div>
  );
}

/**
 * The buyer board (`kind` po): vendor carts released as draft POs. With `kind`
 * to it is the transfer board (GEN-3026): the same layout, with a source
 * location as the vendor, released as draft TOs.
 */
// fallow-ignore-next-line complexity
export function ReplenishmentPage({
  kind = 'po',
  actions,
}: {
  kind?: BoardKind;
  /** Header controls the route adds, e.g. the transfer route's tabs. */
  actions?: ReactNode;
}) {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);
  const refresh = useRefreshReplenishment();

  const handleRefresh = useCallback(() => {
    refresh.mutate(
      {},
      {
        onSuccess: (data) => {
          const needs = data.needsDetected.toLocaleString();
          const created = data.suggestionsCreated.toLocaleString();
          const updated = data.suggestionsUpdated.toLocaleString();
          const skipped = data.suggestionsSkipped.toLocaleString();
          const duration = Math.round(data.durationMs / 1000);
          // Forward buying (phase 4c) runs on every engine run but only has
          // something to say when a cart was under its minimum, so its
          // counters are appended rather than always shown. Its dollars are
          // net of any price tier crossed, so they can be negative.
          const dollarsAdded = data.forwardBuyDollarsAdded;
          const signedDollarsAdded = `${dollarsAdded < 0 ? '\u2212' : '+'}${formatCurrency(Math.abs(dollarsAdded))}`;
          const forwardBuy =
            data.forwardBuyCartsConsidered > 0
              ? ` · ${gt('forward buy', {
                  context:
                    'admin refresh toast, label for the forward-buy counters',
                })}: ${data.forwardBuyCartsCleared.toLocaleString()}/${data.forwardBuyCartsConsidered.toLocaleString()} ${gt(
                  'carts cleared',
                  {
                    context:
                      'admin refresh toast, carts the forward-buy pass lifted over their minimum out of the carts it considered',
                  },
                )}, ${data.forwardBuyLinesUplifted.toLocaleString()} ${gt(
                  'lines',
                  {
                    context:
                      'admin refresh toast, lines the forward-buy pass extended',
                  },
                )} (${signedDollarsAdded})`
              : '';
          toast.success(
            gt('Replenishment refreshed', {
              context: 'admin refresh success toast title',
            }),
            {
              description: `${needs} needs · ${created} created · ${updated} updated · ${skipped} skipped (${duration}s)${forwardBuy}`,
            },
          );
        },
        onError: (err) => {
          toast.error(
            gt('Refresh failed', {
              context: 'admin refresh error toast title',
            }),
            { description: err instanceof Error ? err.message : String(err) },
          );
        },
      },
    );
  }, [refresh, gt]);

  const navigate = useNavigate();
  const basePath = BOARD_PATHS[kind];
  const searchParams = useSearch({ strict: false }) as {
    // Shared procurement filters, owned by the parent filter route.
    divisionIds?: string[];
    // Legacy single-division UUID — accepted for back-compat with old links.
    divisionId?: string;
    // Destination warehouses (scoped to selected divisions); shared filter too.
    warehouseIds?: string[];
    // Reveal trashed (rejected) suggestions board-wide.
    showTrashed?: boolean;
    // Hide lines released since the last refresh (shown by default).
    hideReleased?: boolean;
    supplierId?: string;
    // The transfer board's selected source location (in place of supplierId).
    sourceId?: string;
    // Selected cart's ship-to / destination warehouse (the master row).
    shipTo?: string;
    // Open suggestion in the detail modal.
    suggestionId?: string;
    // The cart line shown in the Line section (a suggestion id).
    line?: string;
    // The Line section's open tab (GEN-3285).
    lineTab?: string;
    // Master-table server sort key + direction, and the 1-based page.
    sort?: string;
    sortDir?: string;
    page?: number;
    // Comma-separated line-type filter and the vendor filter.
    lineTypes?: string;
    // One row per vendor (summed across ship-tos) instead of per cart.
    groupBy?: string;
    vendorIds?: string[];
    // The transfer board's source-location filter.
    sourceIds?: string[];
    // Buyer-code filter (GAINS Receiving Planner).
    buyers?: string[];
    // Shared procurement filters the board applies to its lines (see
    // utils/boardSharedFilters); the filter route owns them.
    warehouseTypes?: string[];
    skuSearch?: string;
    textSearch?: string;
    stockStatuses?: string[];
    popularityCodes?: string[];
    // Order-readiness filter.
    ready?: ReadinessState[];
    // Days-on-order filter (GEN-3151).
    minAge?: number;
  };
  // Master-table filters, sort and page live in the URL; any change other than
  // a page step goes back to page 1.
  const patchSearch = useCallback(
    (patch: Record<string, unknown>, keepPage = false) => {
      void navigate({
        to: basePath,
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          ...(keepPage ? {} : { page: undefined }),
          ...patch,
        }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate, basePath],
  );
  const { sort, direction } = parseBuyingSort(
    searchParams.sort,
    searchParams.sortDir,
  );
  const lineTypes = useMemo(
    () => parseLineTypes(searchParams.lineTypes),
    [searchParams.lineTypes],
  );
  const pageOffset = pageToOffset(searchParams.page);
  const page = pageOffset / BUYING_ORDERS_PAGE_SIZE + 1;
  const vendorIds = searchParams.vendorIds ?? [];
  const sourceIds = searchParams.sourceIds ?? [];
  const buyerCodes = searchParams.buyers ?? [];
  const sharedFilters = useBoardSharedFilters(searchParams);
  const readiness = searchParams.ready ?? [];
  const minDaysOnOrder = searchParams.minAge;
  const groupBy = parseGroupBy(searchParams.groupBy);
  const handleSortChange = useCallback(
    (clicked: BuyingOrderSort) =>
      patchSearch(
        serializeBuyingSort(nextBuyingSort({ sort, direction }, clicked)),
      ),
    [patchSearch, sort, direction],
  );
  // The buyer's own division pick: `divisionIds` (shared with the forecast
  // screens) or the legacy single `divisionId` from old links. Empty = none
  // picked, and the board falls back to its default below without writing it.
  const urlDivisionIds: string[] = searchParams.divisionIds?.length
    ? searchParams.divisionIds
    : searchParams.divisionId
      ? [searchParams.divisionId]
      : [];
  const selectedOwner = selectedOwnerFor(kind, searchParams);
  const selectedOwnerId = selectedOwner && cartOwnerId(selectedOwner);

  const setDivisionIds = useCallback(
    (ids: string[]) => {
      void navigate({
        to: basePath,
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          divisionIds: ids.length > 0 ? ids : undefined,
          page: undefined,
          // Drop the legacy single-value param once the user touches the picker.
          divisionId: undefined,
        }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate, basePath],
  );

  // Remember the buyer's last-used division selection across sessions. The URL
  // stays the shareable source of truth; this preference only seeds the default
  // when a fresh visit has no `divisionIds` in the URL (see the effect below).
  const divisionFilterPref = useUserPreference('procurementDivisionFilter');
  const upsertPreference = useUpsertUserPreference();
  const savedDivisionIds = useMemo<string[]>(() => {
    const ids = divisionFilterPref.data?.value?.divisionIds;
    return Array.isArray(ids)
      ? ids.filter((id): id is string => typeof id === 'string')
      : [];
  }, [divisionFilterPref.data]);

  // User-driven division change (from the picker): update the URL and persist
  // the choice. The auto-default effect calls `setDivisionIds` directly so it
  // never overwrites the saved preference with a fallback value.
  const handleDivisionChange = useCallback(
    (ids: string[]) => {
      setDivisionIds(ids);
      upsertPreference.mutate({
        key: 'procurementDivisionFilter',
        value: { divisionIds: ids },
      });
    },
    [setDivisionIds, upsertPreference],
  );

  // Master-table row selection drives the detail params: a row sets
  // `supplierId` (or the transfer board's `sourceId`) + `shipTo`
  // (AssignmentBoard). Clicking the selected row again deselects.
  const selection: BuyingSelectionParams = useMemo(
    () => ({
      supplierId: searchParams.supplierId,
      sourceId: searchParams.sourceId,
      shipTo: searchParams.shipTo,
    }),
    [searchParams.supplierId, searchParams.sourceId, searchParams.shipTo],
  );
  const clearSelection = useCallback(
    () =>
      patchSearch(
        {
          supplierId: undefined,
          sourceId: undefined,
          shipTo: undefined,
          line: undefined,
        },
        true,
      ),
    [patchSearch],
  );
  const selectRow = useCallback(
    (row: BuyingOrderRow) => {
      if (isRowSelected(row, selection)) clearSelection();
      else patchSearch({ ...selectionForRow(row), line: undefined }, true);
    },
    [selection, clearSelection, patchSearch],
  );
  // A line click shows that line in the Line section; clicking it again clears.
  const selectedLineId = searchParams.line;
  // The Line section's tab: the link's, else the viewer's last (GEN-3285).
  const lineTab = resolveLineTab(searchParams.lineTab, readStoredLineTab());
  const selectLineTab = (tab: LineTab) => {
    storeLineTab(tab);
    // Written even for At a glance, so a shared link opens that tab and a
    // failed storage write can't bring back the last one.
    patchSearch({ lineTab: tab }, true);
  };
  const [selectedLineCount, setSelectedLineCount] = useState(0);
  const selectLine = (suggestionId: string) =>
    patchSearch(
      { line: suggestionId === selectedLineId ? undefined : suggestionId },
      true,
    );
  const hasSelectedCart = selectedOwner != null;
  const suggestionOpen = searchParams.suggestionId != null;
  const [search, setSearch] = useState('');
  const { data: divisionsData } = useDivisions(true, true);

  const divisionOptions: MultiSelectOption[] = useMemo(
    () =>
      (divisionsData?.divisions ?? []).map((d) => ({
        id: d.id,
        label: d.name,
      })),
    [divisionsData],
  );

  const { data: warehousesData } = useWarehouses(true);

  // Derived, never written to the URL — see defaultDivisionIds.
  const defaultDivisions = defaultDivisionIds({
    divisions: divisionOptions,
    urlWarehouseIds: searchParams.warehouseIds ?? [],
    warehouses: warehousesData?.warehouses,
    savedDivisionIds,
    savedPending: divisionFilterPref.isPending,
  });
  const selectedDivisionIds =
    urlDivisionIds.length > 0 ? urlDivisionIds : defaultDivisions;

  const selectedDivisionOptions = useMemo<MultiSelectOption[]>(
    () =>
      selectedDivisionIds
        .map((id) => divisionOptions.find((o) => o.id === id))
        .filter((o): o is MultiSelectOption => o != null),
    [divisionOptions, selectedDivisionIds],
  );

  // ── Warehouse (destination) filter ──────────────────────────────────────
  // A second multi-select scoped to the warehouses inside the selected
  // divisions. Filters suggestions on `destination_warehouse_id`. URL is the
  // shareable source of truth; the saved preference seeds it on a fresh visit.
  const warehouseFilterPref = useUserPreference('procurementWarehouseFilter');

  const savedWarehouseIds = useMemo<string[]>(() => {
    const ids = warehouseFilterPref.data?.value?.warehouseIds;
    return Array.isArray(ids)
      ? ids.filter((id): id is string => typeof id === 'string')
      : [];
  }, [warehouseFilterPref.data]);

  // Options scoped to the selected divisions. Grouped by division name when
  // more than one division is in play so the list stays legible.
  const warehouseOptions = useMemo<MultiSelectOption[]>(() => {
    const sel = new Set(selectedDivisionIds);
    const multi = selectedDivisionIds.length > 1;
    return (warehousesData?.warehouses ?? [])
      .filter((w) => w.division != null && sel.has(w.division.id))
      .map((w) => ({
        id: w.id,
        label: warehouseLabel(w),
        group: multi ? w.division?.name : undefined,
      }));
  }, [warehousesData, selectedDivisionIds]);

  // The transfer board's source filter: any location can ship, whatever the
  // division scope, so it lists every warehouse.
  const sourceOptions: MultiSelectOption[] = (
    warehousesData?.warehouses ?? []
  ).map((w) => ({ id: w.id, label: warehouseLabel(w) }));

  // hub/branch type, shown as a suffix in the option row (avoids threading the
  // type through MultiSelectOption.value, which is loosely typed).
  const warehouseTypeById = useMemo(
    () =>
      new Map(
        (warehousesData?.warehouses ?? []).map((w) => [w.id, w.type] as const),
      ),
    [warehousesData],
  );

  // Selected warehouses: URL wins, else the saved preference. Always narrowed
  // to the current division scope so a division change transparently drops
  // orphaned picks (no separate prune effect needed). Don't narrow until the
  // warehouse list has loaded, or we'd briefly wipe a valid saved selection.
  const warehousesLoaded = warehousesData != null;
  // The board requires a warehouse scope: queries never run with an empty
  // selection. So when the URL carries no explicit pick we fall back to the
  // saved preference, and failing that default to *every* warehouse in the
  // selected divisions ("all in division" — same cost as the old division-only
  // query). The only way this resolves empty is a division with no warehouses,
  // which the render gate turns into a default screen.
  const selectedWarehouseIds = useMemo<string[]>(() => {
    // Explicit URL selection wins (shareable links).
    if (searchParams.warehouseIds !== undefined) {
      const ids = searchParams.warehouseIds;
      if (!warehousesLoaded) return ids;
      const allowed = new Set(warehouseOptions.map((o) => o.id));
      return ids.filter((id) => allowed.has(id));
    }
    // No URL selection — can't decide a default until the warehouse list is in.
    // Returning [] keeps the query gate closed (no premature fetch).
    if (!warehousesLoaded) return [];
    const allowed = new Set(warehouseOptions.map((o) => o.id));
    const validSaved = savedWarehouseIds.filter((id) => allowed.has(id));
    if (validSaved.length > 0) return validSaved;
    return warehouseOptions.map((o) => o.id);
  }, [
    searchParams.warehouseIds,
    savedWarehouseIds,
    warehousesLoaded,
    warehouseOptions,
  ]);

  // Gating + the "all selected ⇒ omit the param" optimization. When every
  // in-scope warehouse is selected the division filter alone is equivalent, so
  // we send no `warehouseIds` (smaller payload, identical query). The board
  // only renders / queries when a selection exists.
  const hasWarehouseSelection = selectedWarehouseIds.length > 0;
  const allWarehousesSelected =
    warehouseOptions.length > 0 &&
    selectedWarehouseIds.length === warehouseOptions.length;

  const selectedWarehouseOptions = useMemo<MultiSelectOption[]>(
    () =>
      selectedWarehouseIds
        .map((id) => warehouseOptions.find((o) => o.id === id))
        .filter((o): o is MultiSelectOption => o != null),
    [warehouseOptions, selectedWarehouseIds],
  );

  const setWarehouseIds = useCallback(
    (ids: string[]) => {
      void navigate({
        to: basePath,
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          // Every warehouse in the division is no warehouse filter at all,
          // and the forecast screens would read it as a list of N branches.
          page: undefined,
          warehouseIds:
            ids.length > 0 && ids.length < warehouseOptions.length
              ? ids
              : undefined,
        }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate, basePath, warehouseOptions.length],
  );

  const handleWarehouseChange = useCallback(
    (ids: string[]) => {
      setWarehouseIds(ids);
      upsertPreference.mutate({
        key: 'procurementWarehouseFilter',
        value: { warehouseIds: ids },
      });
    },
    [setWarehouseIds, upsertPreference],
  );

  // Reveal trashed (rejected) suggestions board-wide. URL-only working toggle.
  const showTrashed = searchParams.showTrashed === true;
  const setShowTrashed = useCallback(
    (next: boolean) => {
      void navigate({
        to: basePath,
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          showTrashed: next ? true : undefined,
        }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate, basePath],
  );
  const includeTrashed = showTrashed || undefined;

  // Lines released since the last refresh stay on the board, marked released,
  // until a one-click toggle hides them (GEN-3119). URL-only working toggle.
  const showReleased = searchParams.hideReleased !== true;
  // Through patchSearch so the page resets: hiding lines can drop carts.
  const setShowReleased = (next: boolean) =>
    patchSearch({ hideReleased: next ? undefined : true });

  // Omit the param when every in-scope warehouse is selected (division filter
  // alone is equivalent); otherwise send the narrowed set.
  const warehouseIdsParam =
    hasWarehouseSelection && !allWarehousesSelected
      ? selectedWarehouseIds
      : undefined;

  const buyerCodesParam = buyerCodes.length > 0 ? buyerCodes : undefined;

  const debouncedSearch = useDebounce(search, 300);
  const buyingOrders = useBuyingOrders(
    {
      divisionIds: selectedDivisionIds,
      warehouseIds: warehouseIdsParam,
      ...ownerListFilters(kind, { vendorIds, sourceIds, readiness }),
      buyerCodes: buyerCodesParam,
      ...sharedFilters,
      minDaysOnOrder,
      lineTypes: serializeLineTypes(lineTypes) ? lineTypes : undefined,
      orderTypes: [kind],
      includeReleased: showReleased,
      groupBy,
      search: debouncedSearch.trim() || undefined,
      sort,
      sortDirection: direction,
      limit: BUYING_ORDERS_PAGE_SIZE,
      offset: pageOffset,
    },
    // Both a division and a warehouse scope are required before fetching.
    selectedDivisionIds.length > 0 && hasWarehouseSelection,
  );
  const buyingRows = useMemo(
    () => buyingOrders.data?.rows ?? [],
    [buyingOrders.data?.rows],
  );
  const buyingTotalRows = buyingOrders.data?.totalRows ?? 0;
  const orderColumnConfig = useBuyerBoardColumnConfig('orders', kind);
  const orderColumns = useColumnsHiddenByFilters(
    filterColumnsShownByView(
      orderFilterColumns({
        vendorIds: kind === 'po' ? vendorIds : [],
        buyerCodes,
        warehouseIds: warehouseIdsParam ?? [],
        vendorGrouping: groupBy === 'vendor',
      }),
      orderColumnConfig.columns,
    ),
    buyingRows,
    { complete: buyingRows.length >= buyingTotalRows },
  );

  const buyerCodeList = useBuyerCodes(
    { divisionIds: selectedDivisionIds, warehouseIds: warehouseIdsParam },
    selectedDivisionIds.length > 0 && hasWarehouseSelection,
  );
  const freshness = useBoardFreshness(
    { divisionIds: selectedDivisionIds },
    selectedDivisionIds.length > 0,
  );
  const buyerOptions = useMemo(
    () =>
      withSelectedCodes(
        buyerCodeList.data?.codes ?? [],
        searchParams.buyers ?? [],
      ).map((code) => ({ id: code, label: code })),
    [buyerCodeList.data?.codes, searchParams.buyers],
  );
  // The forecast grid's option lists (read under the board's permission), so
  // the shared filters offer the same values on every procurement screen.
  const sharedFilterOptions = useBuyingFilterOptions();
  const sharedFilterLabels = useDemandFilterLabels();
  const stockStatusLabel = useStockStatusLabel();
  const sharedFilterSearch = {
    warehouseTypes: searchParams.warehouseTypes,
    skuSearch: searchParams.skuSearch,
    textSearch: searchParams.textSearch,
    stockStatuses: searchParams.stockStatuses,
    popularityCodes: searchParams.popularityCodes,
  };
  const readinessOptions = useReadinessOptions();

  const exportSuggestions = useExportReplenishmentSuggestions();

  const confirmCarts = useConfirmCarts();
  const confirmTransfers = useConfirmTransfers();
  // Below-MOV carts returned by a create attempt, held for the override dialog.
  const [movBlock, setMovBlock] = useState<{
    blocked: ConfirmCartsResponse['blockedBelowMov'];
    carts: ConfirmCartsInput['carts'];
    release: ReleaseSelection;
    // # of orders that DID land in the same submission (the non-blocked carts),
    // so the dialog can lead with that instead of looking like a full failure.
    alreadyCreatedCount: number;
  } | null>(null);
  const [movOverrideComment, setMovOverrideComment] = useState('');
  // Cart-scoped "Create orders" are-you-sure prompt (+ pre-flight summary).
  const [createPrompt, setCreatePrompt] = useState<{
    owner: CartOwner;
    shipToWarehouseId: string;
    summary: CartCreateSummary;
    release: ReleaseSelection;
    // The freight and sending the buyer changed in the dialog (PO carts).
    choice: CartReleaseChoice;
  } | null>(null);
  // Carts with an in-flight create (disables their button + shows a spinner).
  const [creatingCartKeys, setCreatingCartKeys] = useState<Set<string>>(
    new Set(),
  );

  // Release the in-cart lines of one or more supplier×ship-to carts: into a
  // new draft PO, or added to an open one. Only the listed lines are released,
  // so lines left out of the cart stay put. Released lines flip to 'accepted'
  // and drop off the board on the next refetch. Below-MOV carts pause for override.
  const runConfirmCarts = useCallback(
    (
      carts: ConfirmCartsInput['carts'],
      release: ReleaseSelection,
      acknowledgeBelowMov = false,
      belowMovOverrideComment?: string,
    ) => {
      if (carts.length === 0) return;
      setCreatingCartKeys((prev) => {
        const next = new Set(prev);
        carts.forEach((c) =>
          next.add(cartKey(c.supplierId, c.shipToWarehouseId)),
        );
        return next;
      });
      confirmCarts.mutate(
        {
          carts,
          divisionIds:
            selectedDivisionIds.length > 0 ? selectedDivisionIds : undefined,
          suggestionIds: release.suggestionIds,
          appendToDraftPurchaseOrderId:
            release.appendToDraftPurchaseOrderId ?? undefined,
          acknowledgeBelowMov,
          belowMovOverrideComment:
            acknowledgeBelowMov && belowMovOverrideComment
              ? belowMovOverrideComment
              : undefined,
        },
        {
          onSuccess: (res) => {
            const appended = res.appendedPurchaseOrders ?? [];
            const createdCount =
              res.createdPurchaseOrders.length +
              res.createdTransferOrders.length +
              appended.length;
            const hasBlock = res.blockedBelowMov.length > 0;
            const created = releaseCreatedMessage(gt, {
              purchaseOrders: res.createdPurchaseOrders.length,
              transferOrders: res.createdTransferOrders.length,
            });
            if (appended.length > 0 && !hasBlock) {
              toast.success(
                gt('Added {count} line(s) to the open PO', {
                  context: 'release add to open purchase order toast',
                  count: String(appended[0]?.addedLineCount ?? 0),
                }),
              );
            }
            if (created !== null && !hasBlock) toast.success(created);
            if (hasBlock) {
              setMovOverrideComment('');
              setMovBlock({
                blocked: res.blockedBelowMov,
                carts,
                release,
                alreadyCreatedCount: createdCount,
              });
            } else {
              setMovBlock(null);
              setMovOverrideComment('');
            }
          },
          // A refused release says why: an open PO that can't take lines, or
          // a freight or sending pick the server rejects.
          onError: (error) =>
            toast.error(
              error instanceof ORPCError &&
                (error.code === 'CONFLICT' || error.code === 'BAD_REQUEST')
                ? error.message
                : gt('Failed to create orders', {
                    context: 'replenishment create orders error toast',
                  }),
            ),
          onSettled: () => {
            setCreatingCartKeys((prev) => {
              const next = new Set(prev);
              carts.forEach((c) =>
                next.delete(cartKey(c.supplierId, c.shipToWarehouseId)),
              );
              return next;
            });
          },
        },
      );
    },
    [confirmCarts, selectedDivisionIds, gt],
  );

  // Release one transfer cart's approved lines (source → destination) as a
  // draft TO. Transfers have no vendor minimum, so nothing pauses.
  // react-doctor-disable-next-line react-doctor/react-compiler-no-manual-memoization -- a dependency of handleCreateCart's useCallback, like runConfirmCarts beside it.
  const runConfirmTransfer = useCallback(
    (
      sourceWarehouseId: string,
      destinationWarehouseId: string,
      release: ReleaseSelection,
    ) => {
      const key = cartKey(sourceWarehouseId, destinationWarehouseId);
      setCreatingCartKeys((prev) => new Set(prev).add(key));
      confirmTransfers.mutate(
        {
          carts: [{ sourceWarehouseId, destinationWarehouseId }],
          divisionIds:
            selectedDivisionIds.length > 0 ? selectedDivisionIds : undefined,
          suggestionIds: release.suggestionIds,
        },
        {
          onSuccess: (res) => {
            const created = releaseCreatedMessage(gt, {
              purchaseOrders: 0,
              transferOrders: res.createdTransferOrders.length,
            });
            if (created !== null) toast.success(created);
          },
          onError: () =>
            toast.error(
              gt('Failed to create the transfer order', {
                context: 'transfer board release error toast',
              }),
            ),
          onSettled: () =>
            setCreatingCartKeys((prev) => {
              const next = new Set(prev);
              next.delete(key);
              return next;
            }),
        },
      );
    },
    [confirmTransfers, selectedDivisionIds, gt],
  );

  // Per-cart "Create orders" routes through an are-you-sure dialog so nothing
  // is minted until the buyer confirms intent.
  const handleCreateCart = useCallback(
    (
      owner: CartOwner,
      shipToWarehouseId: string,
      summary: CartCreateSummary,
      releaseSuggestionIds: string[],
    ) =>
      setCreatePrompt({
        owner,
        shipToWarehouseId,
        summary,
        release: {
          suggestionIds: releaseSuggestionIds,
          appendToDraftPurchaseOrderId: null,
        },
        choice: {},
      }),
    [],
  );

  const confirmCreatePrompted = useCallback(() => {
    if (!createPrompt) return;
    const { owner, shipToWarehouseId, release, choice } = createPrompt;
    if (owner.kind === 'to') {
      runConfirmTransfer(owner.sourceWarehouseId, shipToWarehouseId, release);
    } else {
      runConfirmCarts(
        [{ supplierId: owner.supplierId, shipToWarehouseId, ...choice }],
        release,
      );
    }
    setCreatePrompt(null);
  }, [createPrompt, runConfirmCarts, runConfirmTransfer]);

  // Re-send the original selection with explicit acknowledgement. A blocked
  // cart can be one the buyer never selected (a make's component vendor), so
  // the blocked list is not a selection, and an append target belongs to the
  // selected cart. Each cart keeps the freight and sending picked for it.
  const handleConfirmBelowMov = useCallback(() => {
    if (!movBlock) return;
    runConfirmCarts(
      movBlock.carts,
      movBlock.release,
      true,
      movOverrideComment.trim(),
    );
  }, [movBlock, movOverrideComment, runConfirmCarts]);

  // Keyboard on the master table: j/k (or the arrows) move a row cursor, Enter
  // opens the cursor row, Esc closes the detail. Typing surfaces and dialogs
  // keep their own keys (see masterKeyIntent).
  const [rawCursorKey, setCursorKey] = useState<string | null>(null);
  const rowKeys = useMemo(() => buyingRows.map(buyingRowKey), [buyingRows]);
  // Only a row that is on screen can hold the cursor (page change, filters).
  // Derived, not synced back into state: a stale raw key is simply ignored.
  const cursorKey = resolveCursor(rowKeys, rawCursorKey);
  const selectedKey = useMemo(() => {
    const row = buyingRows.find((r) => isRowSelected(r, selection));
    return row ? buyingRowKey(row) : null;
  }, [buyingRows, selection]);
  useEffect(() => {
    if (suggestionOpen) return;
    const openCursorRow = (e: KeyboardEvent) => {
      const row = buyingRows.find((r) => buyingRowKey(r) === cursorKey);
      if (row === undefined || isRowSelected(row, selection)) return;
      e.preventDefault();
      patchSearch({ ...selectionForRow(row), line: undefined }, true);
    };
    const moveCursor = (e: KeyboardEvent, intent: MasterKeyIntent) => {
      if (intent !== 'next' && intent !== 'prev') return;
      e.preventDefault();
      const next = stepCursor(rowKeys, cursorKey ?? selectedKey, intent);
      setCursorKey(next);
      if (next === null) return;
      document
        .querySelector(`[data-row-id="${CSS.escape(next)}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    };
    const run: Record<
      MasterKeyAction,
      (e: KeyboardEvent, intent: MasterKeyIntent) => void
    > = {
      'clear-line': () => patchSearch({ line: undefined }, true),
      'clear-cart': () => clearSelection(),
      open: openCursorRow,
      step: moveCursor,
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const intent = masterKeyIntent(e);
      if (intent === null) return;
      const action = masterKeyAction(intent, {
        lineSelected: selectedLineId != null,
        cartOpen: hasSelectedCart,
      });
      if (action !== null) run[action](e, intent);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    suggestionOpen,
    selectedLineId,
    hasSelectedCart,
    clearSelection,
    buyingRows,
    rowKeys,
    cursorKey,
    selectedKey,
    selection,
    patchSearch,
  ]);

  // The master row behind the open detail, when it is on the current page.
  const selectedRow = useMemo(
    () => buyingRows.find((r) => isRowSelected(r, selection)) ?? null,
    [buyingRows, selection],
  );
  // The open cart's line query, shared with AssignmentBoard so the Line
  // section reads the selected line from the same cache entry.
  const cartQuery = {
    owner: selectedOwner ?? { kind: 'po', supplierId: '' },
    divisionIds:
      selectedDivisionIds.length > 0 ? selectedDivisionIds : undefined,
    warehouseIds: warehouseIdsParam,
    includeTrashed,
    includeReleased: showReleased,
    buyerCodes: buyerCodesParam,
    lineFilters: sharedFilters,
    shipToWarehouseId: searchParams.shipTo,
  };
  const { data: cartLines } = useCartLines(
    cartQuery,
    selectedOwner !== null && selectedLineId != null,
    true,
  );
  // A selected need row (GEN-2938) shows its oldest open SO line below.
  const selectedLine = isNeedId(selectedLineId)
    ? needRepresentativeLine(cartLines?.lines ?? [], selectedLineId)
    : (cartLines?.lines.find((line) => line.suggestionId === selectedLineId) ??
      null);
  const detailPanel =
    selectedOwner === null ? null : (
      // scroll-pt-7: rows and Qty inputs scrolled into view by the keyboard
      // stop below the Lines table's pinned 28px head, not under it.
      <div className="min-h-0 flex-1 overflow-auto scroll-pt-7">
        {searchParams.shipTo == null && (
          <LegacyCloseButton onClose={clearSelection} />
        )}
        <AssignmentBoard
          {...cartQuery}
          owner={selectedOwner}
          designationFilter={kind}
          lineTypes={lineTypes}
          selectedLineId={selectedLineId}
          onSelectLine={selectLine}
          onClearLine={() => patchSearch({ line: undefined }, true)}
          onSelectedCountChange={setSelectedLineCount}
          lineKeysDisabled={suggestionOpen}
          onClose={clearSelection}
          enabled
          creatingCartKeys={creatingCartKeys}
          onCreateCart={(shipToWarehouseId, summary, releaseSuggestionIds) =>
            handleCreateCart(
              selectedOwner,
              shipToWarehouseId,
              summary,
              releaseSuggestionIds,
            )
          }
        />
      </div>
    );

  // Section state: Filters folded to one line, Orders folds when an order is
  // opened and re-opens when the detail closes.
  const [sections, dispatchSections] = useReducer(
    boardSectionsReducer,
    hasSelectedCart,
    initialSections,
  );
  const selectionKey =
    selectedOwnerId === null
      ? null
      : `${selectedOwnerId}:${searchParams.shipTo ?? ''}`;
  // Applied during render (React's "adjust state when a prop changes"
  // pattern), not in an effect: the panel group remounts when the detail
  // appears, and it must mount with Orders already folded.
  const [previousSelectionKey, setPreviousSelectionKey] =
    useState(selectionKey);
  if (previousSelectionKey !== selectionKey) {
    setPreviousSelectionKey(selectionKey);
    const action = selectionChangeAction(previousSelectionKey, selectionKey);
    if (action !== null) dispatchSections(action);
  }
  const setSectionCollapsed = useCallback(
    (section: BoardSectionId, collapsed: boolean) =>
      dispatchSections({ type: 'set', section, collapsed }),
    [],
  );
  // A selected cart does not exist in the other grouping.
  const handleGroupByChange = useCallback(
    (next: BuyingGroupBy) =>
      patchSearch({
        groupBy: serializeGroupBy(next),
        supplierId: undefined,
        sourceId: undefined,
        shipTo: undefined,
      }),
    [patchSearch],
  );

  const lineTypeLabels = useLineTypeOptions();
  const filtersSummary = filtersSummaryLine({
    divisions: {
      selectedLabels: selectedDivisionOptions.map((o) => o.label),
      totalCount: divisionOptions.length,
      allLabel: gt('All divisions', {
        context: 'replenishment division filter summary, nothing narrowed',
      }),
      prefix: gt('Division', {
        context: 'replenishment division filter summary prefix',
      }),
    },
    locations: {
      selectedLabels: selectedWarehouseOptions.map((o) => o.label),
      totalCount: warehouseOptions.length,
      allLabel: gt('All locations', {
        context: 'replenishment location filter summary, nothing narrowed',
      }),
      prefix: gt('Location', {
        context: 'replenishment location filter summary prefix',
      }),
    },
    lineTypes: {
      selectedLabels: lineTypes.map(
        (t) => lineTypeLabels.find((o) => o.id === t)?.label ?? t,
      ),
      totalCount: ALL_LINE_TYPES.length,
      allLabel: gt('All line types', {
        context: 'replenishment line type filter summary, nothing narrowed',
      }),
      prefix: gt('Line type', {
        context: 'replenishment line type filter summary prefix',
      }),
    },
    sharedParts: sharedFiltersSummary(sharedFilters, sharedFilterLabels, {
      stockStatuses: stockStatusLabel,
    }),
    readiness: {
      selectedLabels: readiness.map(
        (s) => readinessOptions.find((o) => o.id === s)?.label ?? s,
      ),
      totalCount: readinessOptions.length,
      allLabel: gt('Any readiness', {
        context:
          'replenishment order readiness filter summary, nothing narrowed',
      }),
      prefix: gt('Readiness', {
        context: 'replenishment order readiness filter summary prefix',
      }),
    },
    minDaysOnOrder,
    daysOnOrderLabel: (days) =>
      gt('On order {days}+ days', {
        context: 'replenishment days-on-order filter summary',
        days: String(days),
      }),
    buyerCodes,
    allBuyersLabel: gt('All buyers', {
      context: 'replenishment buyer filter summary, nothing narrowed',
    }),
    buyersLabel: (codes) =>
      gt('Buyer {codes}', {
        context: 'replenishment buyer filter summary',
        codes,
      }),
    buyersCountLabel: (count) =>
      gt('Buyers: {count}', {
        context: 'replenishment buyer filter summary, many',
        count: String(count),
      }),
    ...ownerFilterSummary(kind, gt, { vendorIds, sourceIds }),
  });

  const selectedOrderSummary =
    selectedRow === null
      ? null
      : orderSummary(selectedRow, {
          kind: () => orderKindTag(kind, gt),
          lines: (count) =>
            count === 1
              ? gt('1 line', { context: 'buying order summary line count' })
              : gt('{count} lines', {
                  context: 'buying order summary line count',
                  count: String(count),
                }),
          locations: (count) =>
            count === 1
              ? gt('1 location', {
                  context: 'buying orders ship-to cell, vendor grouping',
                })
              : gt('{count} locations', {
                  context: 'buying orders ship-to cell, vendor grouping',
                  count: String(count),
                }),
        });

  const exportButton = (
    <ExcelExportButton
      compact
      isPending={exportSuggestions.isPending}
      disabled={selectedDivisionIds.length === 0 || !hasWarehouseSelection}
      className="shrink-0"
      onExport={() =>
        exportSuggestions.mutateAsync({
          divisionIds: selectedDivisionIds,
          warehouseIds: warehouseIdsParam,
          includeTrashed,
          search: search.trim() || undefined,
          view: kind,
        })
      }
    />
  );
  const refreshButton = userIsAdmin ? (
    <Button
      size="sm"
      variant="outline"
      onClick={handleRefresh}
      disabled={refresh.isPending}
      className="h-6 shrink-0 gap-1 whitespace-nowrap px-2 text-xs"
      title={gt('Re-run replenishment engine for this org (admin only)', {
        context: 'admin refresh button tooltip',
      })}
    >
      <ArrowsClockwiseIcon
        className={cn('h-3.5 w-3.5', refresh.isPending && 'animate-spin')}
      />
      {refresh.isPending ? (
        <T context="admin refresh button label running">Refreshing…</T>
      ) : (
        <T context="admin refresh button label">Refresh suggestions</T>
      )}
    </Button>
  ) : null;

  const filtersSection = (
    <>
      <BuyingFiltersHeader
        collapsed={sections.filters}
        onToggle={() =>
          dispatchSections({ type: 'toggle', section: 'filters' })
        }
        controlsId="buyer-board-filters"
        summary={filtersSummary}
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          if (searchParams.page !== undefined) patchSearch({}, false);
        }}
        totalRows={buyingTotalRows}
        vendorGrouping={groupBy === 'vendor'}
        exportButton={exportButton}
        refreshButton={refreshButton}
        freshness={
          freshness.data && (
            <BoardFreshnessIndicator
              divisions={freshness.data.divisions}
              detailed={userIsAdmin}
            />
          )
        }
      />
      {!sections.filters && (
        <div id="buyer-board-filters">
          <BuyingFilterControls
            divisionOptions={divisionOptions}
            selectedDivisionOptions={selectedDivisionOptions}
            onDivisionChange={handleDivisionChange}
            warehouseOptions={warehouseOptions}
            selectedWarehouseOptions={selectedWarehouseOptions}
            warehouseTypeById={warehouseTypeById}
            onWarehouseChange={handleWarehouseChange}
            vendorIds={vendorIds}
            onVendorChange={(ids) =>
              patchSearch({ vendorIds: ids.length > 0 ? ids : undefined })
            }
            ownerFilter={
              kind === 'to' ? (
                <CodeFilter
                  widthClass="w-[170px]"
                  options={sourceOptions}
                  selectedIds={sourceIds}
                  onChange={(ids) =>
                    patchSearch({ sourceIds: ids.length > 0 ? ids : undefined })
                  }
                  label={gt('Source', {
                    context: 'transfer board source location filter label',
                  })}
                  allLabel={gt('All sources', {
                    context:
                      'transfer board source location filter summary, nothing narrowed',
                  })}
                  prefix={gt('Sources', {
                    context: 'transfer board source location filter prefix',
                  })}
                  deferUpdate
                />
              ) : undefined
            }
            showReadiness={kind === 'po'}
            buyerOptions={buyerOptions}
            buyerCodes={buyerCodes}
            onBuyerChange={(codes) =>
              patchSearch({ buyers: codes.length > 0 ? codes : undefined })
            }
            readiness={readiness}
            onReadinessChange={(states) =>
              patchSearch({ ready: states.length > 0 ? states : undefined })
            }
            minDaysOnOrder={minDaysOnOrder}
            onMinDaysOnOrderChange={(days) => patchSearch({ minAge: days })}
            lineTypes={lineTypes}
            onLineTypesChange={(types) =>
              patchSearch({ lineTypes: serializeLineTypes(types) })
            }
            showTrashed={showTrashed}
            onShowTrashedChange={setShowTrashed}
            showReleased={showReleased}
            onShowReleasedChange={setShowReleased}
          />
          <div className="px-2 pb-2">
            <DemandFilterBar
              search={sharedFilterSearch}
              options={sharedFilterOptions.data}
              onChange={(next) => patchSearch(sharedFiltersPatch(next))}
              keys={BOARD_SHARED_FILTER_KEYS}
            />
          </div>
        </div>
      )}
    </>
  );

  // Section header + column header + one compact row, in px.
  const FOLDED_ORDERS_WITH_ROW_PX = 88;
  // Folded with the selected order on this page: Orders keeps that one row
  // (with its column header) so the buyer still sees what is open.
  const showSelectedOnly = sections.orders && selectedRow !== null;
  const ordersBody = buyingOrders.error ? (
    <ErrorMessage error={buyingOrders.error} />
  ) : (
    <BuyingOrdersTable
      rows={showSelectedOnly ? [selectedRow] : buyingRows}
      totalRows={showSelectedOnly ? 1 : buyingTotalRows}
      isLoading={buyingOrders.isLoading}
      sort={sort}
      direction={direction}
      onSortChange={handleSortChange}
      isSelected={(row) => isRowSelected(row, selection)}
      cursorKey={cursorKey}
      onSelect={selectRow}
      page={page}
      onPageChange={(next) =>
        patchSearch({ page: next > 1 ? next : undefined }, true)
      }
      columnVisibility={orderColumns.columnVisibility}
      columnConfig={orderColumnConfig}
    />
  );

  const ordersSection = (
    <>
      <BoardSectionHeader
        label={gt('Orders', { context: 'buying board section label' })}
        collapsed={sections.orders}
        onToggle={() => dispatchSections({ type: 'toggle', section: 'orders' })}
        controlsId="buyer-board-orders"
        summary={
          hasSelectedCart && !showSelectedOnly
            ? (selectedOrderSummary ??
              gt('Order selected', {
                context: 'buying board orders summary, row not on this page',
              }))
            : undefined
        }
      >
        <BuyingGroupByToggle value={groupBy} onChange={handleGroupByChange} />
        <span className="shrink-0 whitespace-nowrap tabular-nums text-muted-foreground">
          {buyingTotalRows.toLocaleString()}
        </span>
        <OrdersViewMenu columns={orderColumns} config={orderColumnConfig} />
      </BoardSectionHeader>
      <div id="buyer-board-orders" className="min-h-0 flex-1 overflow-auto">
        {ordersBody}
      </div>
    </>
  );

  // A need row's id is no suggestion id: wait for its representative SO line.
  const lineSuggestionId =
    selectedLine?.suggestionId ??
    (isNeedId(selectedLineId) ? undefined : selectedLineId);
  const lineSection =
    detailPanel === null || lineSuggestionId == null ? null : (
      <LineDetailSection
        suggestionId={lineSuggestionId}
        line={selectedLine}
        collapsed={sections.line}
        onToggle={() => dispatchSections({ type: 'toggle', section: 'line' })}
        onOpenPlanningDetails={() =>
          patchSearch({ suggestionId: lineSuggestionId }, true)
        }
        onClose={() => patchSearch({ line: undefined }, true)}
        tab={lineTab}
        onTabChange={selectLineTab}
        selectedCount={selectedLineCount}
      />
    );

  const detailSection =
    detailPanel === null ? null : (
      <DetailSection
        collapsed={sections.detail}
        onToggle={() => dispatchSections({ type: 'toggle', section: 'detail' })}
      >
        {detailPanel}
      </DetailSection>
    );

  return (
    <BoardKindContext.Provider value={kind}>
      <Page
        actions={actions}
        title={
          kind === 'po' ? (
            <T context="page title outgoing purchase orders">
              Outgoing Purchase Orders
            </T>
          ) : (
            <T context="page title transfer supply board">Transfer Supply</T>
          )
        }
      >
        {selectedDivisionIds.length > 0 && !warehousesLoaded ? (
          // Wait for the warehouse list before deciding the scope — the board
          // must not query without a warehouse set.
          <SectionSpinner
            variant="block"
            label={gt('Loading warehouses…', {
              context: 'replenishment warehouses loading label',
            })}
          />
        ) : selectedDivisionIds.length > 0 && warehouseOptions.length === 0 ? (
          // Default screen: the selected division(s) have no warehouses, so
          // there's nothing to scope a query to.
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 py-20 text-center">
            <span className="text-base font-medium text-gray-700">
              <T context="replenishment no-warehouses title">
                No warehouses in this division
              </T>
            </span>
            <span className="max-w-md text-sm text-gray-500">
              <T context="replenishment no-warehouses body">
                The selected division has no warehouses to replenish. Pick a
                different division to see suggestions.
              </T>
            </span>
          </div>
        ) : selectedDivisionIds.length > 0 && !hasWarehouseSelection ? (
          // Defensive: divisions chosen, warehouses exist, but none in scope
          // (e.g. a shared link whose warehouseIds fell outside the divisions).
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 py-20 text-center">
            <span className="text-base font-medium text-gray-700">
              <T context="replenishment select-warehouses title">
                Select one or more warehouses
              </T>
            </span>
            <span className="max-w-md text-sm text-gray-500">
              <T context="replenishment select-warehouses body">
                Choose warehouses from the filter above to view replenishment
                suggestions.
              </T>
            </span>
          </div>
        ) : (
          <BuyerBoardLayout
            state={sections}
            onSectionCollapsed={setSectionCollapsed}
            ordersCollapsedPx={
              showSelectedOnly ? FOLDED_ORDERS_WITH_ROW_PX : undefined
            }
            filters={filtersSection}
            orders={ordersSection}
            detail={detailSection}
            line={lineSection}
          />
        )}

        {/* Scope the detail modal to this portal's side: the buys portal shows
          only buy options + a buy-only plan, the transfers portal only transfer.
          Undefined (legacy combined page) shows both. */}
        <SuggestionDetailModal
          side={kind === 'po' ? 'buy' : 'transfer'}
          divisionIds={
            selectedDivisionIds.length > 0 ? selectedDivisionIds : undefined
          }
          warehouseIds={warehouseIdsParam}
          buyerCodes={buyerCodesParam}
          lineFilters={sharedFilters}
        />

        <ConfirmDialog
          open={movBlock !== null}
          onClose={() => {
            setMovBlock(null);
            setMovOverrideComment('');
          }}
          onConfirm={handleConfirmBelowMov}
          loading={confirmCarts.isPending}
          disabled={movOverrideComment.trim().length === 0}
          // Don't auto-close on click — handleConfirmBelowMov's onSuccess closes
          // explicitly. On error the dialog stays open with the blocked-cart
          // context intact so the buyer can retry.
          closeOnConfirm={false}
          dangerous
          wide
          title={
            <span className="text-red-700">
              <T context="below minimum order value warning title">
                ⚠ Below vendor minimum order value
              </T>
            </span>
          }
          confirmText={gt('Confirm anyway', {
            context: 'confirm anyway below mov',
          })}
        >
          <div className="space-y-2 text-sm">
            {movBlock && movBlock.alreadyCreatedCount > 0 && (
              <p className="rounded border border-green-200 bg-green-50 px-2 py-1 text-green-800">
                <T context="below mov partial-success notice">
                  <Var>{movBlock.alreadyCreatedCount}</Var> order(s) from this
                  submission were already created (those that met MOV). The
                  carts below are the ones that didn&apos;t — decide whether to
                  push them through anyway.
                </T>
              </p>
            )}
            <p>
              <T context="below mov warning body">
                These carts are under the vendor&apos;s minimum order value.
                Confirming will create purchase orders below the minimum — the
                vendor may reject or surcharge them.
              </T>
            </p>
            <ul className="space-y-1">
              {(movBlock?.blocked ?? []).map((b) => (
                <li
                  key={`${b.supplierId}:${b.shipToWarehouseId}`}
                  className="rounded border border-red-200 bg-red-50 px-2 py-1 text-red-800"
                >
                  <span className="font-medium">
                    {b.supplierName ?? b.supplierId}
                  </span>{' '}
                  → {b.shipToWarehouseName ?? b.shipToWarehouseId}:{' '}
                  {`$${b.cartTotal.toLocaleString()}`} /{' '}
                  {`$${b.movThreshold.toLocaleString()}`} MOV
                </li>
              ))}
            </ul>
            <div className="space-y-1.5 pt-2">
              <Label htmlFor="mov-override-comment">
                {gt('Why release below the vendor MOV?', {
                  context: 'below mov override comment label',
                })}
              </Label>
              <Textarea
                id="mov-override-comment"
                value={movOverrideComment}
                onChange={(e) => setMovOverrideComment(e.target.value)}
                placeholder={gt(
                  'Vendor approved an exception, forward buy, …',
                  {
                    context: 'below mov override comment placeholder',
                  },
                )}
                rows={3}
              />
            </div>
          </div>
        </ConfirmDialog>

        <ConfirmDialog
          open={createPrompt !== null}
          onClose={() => setCreatePrompt(null)}
          onConfirm={confirmCreatePrompted}
          loading={confirmCarts.isPending || confirmTransfers.isPending}
          title={gt('Release the approved lines in this cart?', {
            context: 'create orders dialog title',
          })}
          confirmText={gt('Release', {
            context: 'create orders dialog action',
          })}
        >
          <div className="space-y-3 text-sm">
            {createPrompt?.owner.kind === 'po' && (
              <ReleaseTargetChoice
                supplierId={createPrompt.owner.supplierId}
                shipToWarehouseId={createPrompt.shipToWarehouseId}
                value={createPrompt.release.appendToDraftPurchaseOrderId}
                onChange={(draftId) =>
                  setCreatePrompt(
                    (prev) =>
                      prev && {
                        ...prev,
                        release: {
                          ...prev.release,
                          appendToDraftPurchaseOrderId: draftId,
                        },
                        choice: {},
                      },
                  )
                }
              />
            )}
            <p className="text-gray-700">
              <T context="create orders dialog summary lead">
                This will create as DRAFTs:
              </T>
            </p>
            <ul className="space-y-1">
              {(createPrompt?.summary.poCount ?? 0) > 0 && (
                <li className="flex items-center justify-between rounded bg-blue-50 px-2 py-1">
                  <span className="font-medium text-blue-800">
                    <Var>{createPrompt?.summary.poCount ?? 0}</Var>{' '}
                    <T context="create orders dialog po count">
                      purchase order
                    </T>
                    {(createPrompt?.summary.buySkuCount ?? 0) > 0 && (
                      <span className="font-normal text-blue-700">
                        {' · '}
                        <Var>{createPrompt?.summary.buySkuCount ?? 0}</Var>{' '}
                        <T context="cart line table column header">SKU</T>
                      </span>
                    )}
                  </span>
                  <span className="tabular-nums font-medium text-blue-800">
                    {formatCurrency(createPrompt?.summary.grandTotal ?? 0)}
                  </span>
                </li>
              )}
              {(createPrompt?.summary.toCount ?? 0) > 0 && (
                <li className="rounded bg-gray-100 px-2 py-1 font-medium text-gray-700">
                  <Var>{createPrompt?.summary.toCount ?? 0}</Var>{' '}
                  {(createPrompt?.summary.toCount ?? 0) === 1
                    ? gt('transfer order', {
                        context: 'create orders dialog to count singular',
                      })
                    : gt('transfer orders', {
                        context: 'create orders dialog to count plural',
                      })}
                  {(createPrompt?.summary.transferSkuCount ?? 0) > 0 && (
                    <span className="font-normal text-gray-500">
                      {' · '}
                      <Var>
                        {createPrompt?.summary.transferSkuCount ?? 0}
                      </Var>{' '}
                      <T context="cart line table column header">SKU</T>
                    </span>
                  )}
                </li>
              )}
            </ul>

            {/* Demand coverage over the selected lines. */}
            {createPrompt &&
              (createPrompt.summary.salesOrderCount > 0 ||
                createPrompt.summary.deficitCount > 0) && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {createPrompt.summary.salesOrderCount > 0 && (
                    <StatChip
                      value={createPrompt.summary.salesOrderCount}
                      tone="bg-red-100 text-red-700"
                    >
                      <T context="mov rail chip so badge">SO</T>
                    </StatChip>
                  )}
                  {createPrompt.summary.deficitCount > 0 && (
                    <StatChip
                      value={createPrompt.summary.deficitCount}
                      tone="bg-amber-100 text-amber-700"
                    >
                      <T context="sort stack dimension label">Deficits</T>
                    </StatChip>
                  )}
                </div>
              )}

            {createPrompt?.owner.kind === 'po' &&
              createPrompt.summary.poCount > 0 && (
                <ReleaseFreightAndSending
                  // A new PO and an open one start from different freight
                  // and sending, so switching the target starts over.
                  key={
                    createPrompt.release.appendToDraftPurchaseOrderId ?? 'new'
                  }
                  supplierId={createPrompt.owner.supplierId}
                  shipToWarehouseId={createPrompt.shipToWarehouseId}
                  orderValue={createPrompt.summary.grandTotal}
                  lines={createPrompt.summary.buyLines}
                  appendToDraftPurchaseOrderId={
                    createPrompt.release.appendToDraftPurchaseOrderId
                  }
                  onChange={(choice) =>
                    setCreatePrompt((prev) => prev && { ...prev, choice })
                  }
                />
              )}

            {/* MOV / freight progress for the PO value. Both are always shown —
              they're different constraints: MOV blocks the create, prepaid
              freight only costs money. */}
            {createPrompt && createPrompt.summary.poCount > 0 && (
              <div className="space-y-2.5 rounded border border-gray-100 bg-gray-50/60 p-2">
                <ThresholdBar
                  kind="hard"
                  label={gt('Minimum order value (MOV)', {
                    context: 'create orders dialog mov label',
                  })}
                  hint={
                    createPrompt.summary.movThreshold == null
                      ? gt('No vendor minimum', {
                          context: 'create orders dialog mov none',
                        })
                      : gt(
                          'Below the vendor minimum — creating needs an override',
                          {
                            context: 'create orders dialog mov hard hint',
                          },
                        )
                  }
                  value={createPrompt.summary.grandTotal}
                  threshold={createPrompt.summary.movThreshold}
                />
                <ThresholdBar
                  kind="soft"
                  label={gt('Prepaid-freight minimum', {
                    context: 'create orders dialog freight label',
                  })}
                  hint={
                    createPrompt.summary.freightThreshold == null
                      ? gt('No prepaid-freight threshold', {
                          context: 'create orders dialog freight none',
                        })
                      : gt(
                          "Short of prepaid freight — the order still goes through, you'll just pay freight",
                          {
                            context: 'create orders dialog freight soft hint',
                          },
                        )
                  }
                  value={createPrompt.summary.grandTotal}
                  threshold={createPrompt.summary.freightThreshold}
                />
              </div>
            )}

            <p className="text-xs text-gray-500">
              {/* A transfer cart has no vendor minimum and never pauses. */}
              {createPrompt?.owner.kind === 'to' ? (
                <T context="create transfer orders dialog caveat">
                  Only approved lines go on the order. Unapproved and Trouble
                  lines are left untouched.
                </T>
              ) : (
                <T context="create orders dialog caveat">
                  Only approved lines go on the order. Unapproved and Trouble
                  lines are left untouched; a cart below the vendor minimum
                  pauses for explicit override.
                </T>
              )}
            </p>
          </div>
        </ConfirmDialog>
      </Page>
    </BoardKindContext.Provider>
  );
}

// The Outgoing Purchase Orders portal (hub buys). Transfers moved to their own
// dedicated staged-transfers monitor — see InternalTransfersPage.tsx — since
// the auto-stage model removed the manual transfer-assignment flow.
export function VendorPurchaseOrdersPage() {
  return <ReplenishmentPage />;
}
